import { LocalesEnum, RegionsEnum } from 'src/generated/operations.ts';
import { DamperType, RoundDamperBlades, UserInput } from 'src/actuators/user-input/types.ts';
import { ActuatorFormFields, MapFieldToLabel } from 'src/actuators/common/UserInputFields.ts';
import { ACTUATORS_DECIMAL_DIGITS, MESSAGES } from 'src/modules/common/constants.ts';
import { mapAirflowNames, mapBladeTypeNames } from 'src/actuators/constants.ts';
import { numberFormatter } from 'src/utils/numberFormatter.ts';

const getUniqFields = (region: RegionsEnum, damper : DamperType): ActuatorFormFields[] => {
    if (region === RegionsEnum.Am) {
        return damper === DamperType.Round ? [
            'air',
            'crankArmLength',
        ] : [
            'blade',
            'air',
            'crankArmLength',
        ];
    }
    return damper === DamperType.Round ? [
        'roundDumper',
        'safety',
    ] : [
        'blade',
        'safety',
        'crankArmLength',
    ];
};

type Translatable = { key: string } | string;

type Row = [Translatable, Translatable, Translatable];

export const prepareUserInputFormValues = ({
    userInput,
    currentRegion,
    currentLocale,
    isLinear,
} : {
    userInput: UserInput;
    currentRegion: RegionsEnum;
    currentLocale: LocalesEnum;
    isLinear: boolean;
}): Row[] => {
    const MakeTranslatable = (key: string): { key: string } => ({ key });
    const resultLabel = { key: isLinear ? MESSAGES.DamperRequiredForce : MESSAGES.DamperRequiredTorque };
    let resultUnit = { key: isLinear ? MESSAGES.PoundForce : MESSAGES.PoundInch };
    if (userInput.force && userInput.force.length > 0) {
        return [[resultLabel, numberFormatter(userInput.force, currentLocale, ACTUATORS_DECIMAL_DIGITS), resultUnit]];
    }
    if (currentRegion !== RegionsEnum.Am) {
        resultUnit = { key: isLinear ? MESSAGES.Newton : MESSAGES.NewtonMeter };
    }
    const result = numberFormatter(userInput.result, currentLocale, ACTUATORS_DECIMAL_DIGITS);
    const damperTypeValue = MakeTranslatable(userInput.damper === DamperType.Rectangular
        ? MESSAGES.DamperRectangular : MESSAGES.DamperRound);

    const getAreaOrDiameterUnit = () => {
        if (currentRegion === RegionsEnum.Am) {
            return MESSAGES.SquareFeet;
        }

        if (userInput.damper === DamperType.Rectangular) {
            return MESSAGES.SquareMeter;
        }

        return MESSAGES.Millimeter;
    };

    const out: Row[] = [
        [MakeTranslatable(MESSAGES.DamperType), damperTypeValue, ''],
        [MakeTranslatable(MESSAGES.DamperArea), numberFormatter(
            userInput.areaOrDiameter,
            currentLocale,
            ACTUATORS_DECIMAL_DIGITS,
        ), MakeTranslatable(getAreaOrDiameterUnit())],
        ...(getUniqFields(currentRegion, userInput.damper).filter((key) => {
            if (!isLinear && key === 'crankArmLength') {
                return false;
            }
            const v = userInput[key];
            return v !== null && v !== '';
        }).map<Row>((key) => {
            let unit: Translatable = '';
            let v: Translatable = `${userInput[key]}`;
            if (key === 'roundDumper') {
                v = MakeTranslatable(userInput.roundDumper === RoundDamperBlades.BladeWithoutSeals
                    ? MESSAGES.BladeWithoutSeals : MESSAGES.BladeWithSeals);
            }
            if (key === 'blade' && userInput.blade !== null) {
                v = MakeTranslatable(mapBladeTypeNames[userInput.blade]);
            }
            if (key === 'crankArmLength') {
                v = numberFormatter(`${v}`, currentLocale, ACTUATORS_DECIMAL_DIGITS);
                unit = { key: currentRegion === RegionsEnum.Am
                    ? MESSAGES.Inch : MESSAGES.Millimeter };
            }
            if (key === 'safety') {
                v = numberFormatter(`${v}`, currentLocale, ACTUATORS_DECIMAL_DIGITS);
                unit = { key: MESSAGES.Percent };
            }
            if (key === 'air') {
                v = MakeTranslatable(mapAirflowNames[userInput.air]);
            }
            return [{ key: MapFieldToLabel[key] }, v, unit];
        })),
    ];
    if (userInput.result && !(currentRegion !== RegionsEnum.Am && userInput.damper === DamperType.Round)) {
        out.push([resultLabel, result, resultUnit]);
    }
    return out;
};
